import React from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import useEscapeKey from '../hooks/useEscapeKey';
import './BaseModal.css';

/**
 * BaseModal — canonical modal structure for all modals in the app.
 *
 * Enforces: white header (icon + title + ×), scrollable body, fixed footer.
 * This is the single source of truth for modal layout. Use it for all new modals.
 *
 * Props:
 *   isOpen      {boolean}     — whether the modal is visible
 *   onClose     {function}    — called when overlay or × is clicked
 *   title       {string}      — modal heading text
 *   icon        {string}      — MDI icon path (optional)
 *   iconColor   {string}      — icon color (default: #451f78 purple)
 *   maxWidth    {number}      — max-width in px (default: 520)
 *   children                  — body content (form fields, lists, etc.)
 *   footer                    — footer content (buttons)
 *
 * Usage:
 *   import BaseModal from '../../components/BaseModal';
 *   import { mdiPencil } from '@mdi/js';
 *
 *   <BaseModal
 *     isOpen={showModal}
 *     onClose={() => setShowModal(false)}
 *     title="Edit Something"
 *     icon={mdiPencil}
 *     footer={
 *       <>
 *         <button className="base-modal-cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
 *         <button className="base-modal-save-btn" onClick={handleSave} disabled={isSaving}>
 *           {isSaving ? 'Saving...' : 'Save'}
 *         </button>
 *       </>
 *     }
 *   >
 *     <p>Body content here</p>
 *   </BaseModal>
 *
 * Button colour overrides (only colour — not structure):
 *   If the feature context uses turquoise instead of purple, override in the
 *   feature's own CSS file:
 *     .my-feature-page .base-modal-save-btn { background: var(--dl-color-primary); }
 *     .my-feature-page .base-modal-save-btn:hover:not(:disabled) { background: var(--dl-color-primary-dark); }
 */
const BaseModal = ({
    isOpen,
    onClose,
    title,
    icon,
    iconColor = '#451f78',
    maxWidth = 520,
    children,
    footer,
    className = '',
}) => {
    // Close on Escape — applies to every modal in the app since they all use BaseModal.
    useEscapeKey(isOpen, onClose);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={`base-modal-overlay ${className}`.trim()} onClick={onClose}>
            <div
                className="base-modal"
                style={{ maxWidth }}
                onClick={e => e.stopPropagation()}
            >
                <div className="base-modal-header">
                    <div className="base-modal-header-content">
                        {icon && <Icon path={icon} size={1} color={iconColor} />}
                        <h2>{title}</h2>
                    </div>
                    <button className="base-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="base-modal-body">
                    {children}
                </div>

                {footer && (
                    <div className="base-modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default BaseModal;
